

























































































































































































































import ReservationCreateModal from "@/components/modals/reservation-create.vue";
import Vue from "vue";
import Component from "vue-class-component";
import Card from "@/components/layout/card.vue";
import DateRangePicker from "@/components/pickers/date-range-picker.vue";
import DataTable from "@/components/data/data-table.vue";
import { Prop, Watch } from "vue-property-decorator";
import { DateTime } from "luxon";
import {
    reservationTableActions,
    reservationTableFields,
    reservationTableFilters,
    reservationTableResult,
} from "@/components/tables/reservations/reservation-table-helpers";
import {
    CheckinFilterValues,
    ReservationsTableInitialFilters,
    ReservationTableFilters,
    ReservationTableResult,
} from "@/components/tables/reservations/reservation-table-types";
import { reservationService } from "@/services/reservation";
import { userService } from "@/services/user";
import { officeService } from "@/services/office";
import { floorService } from "@/services/floor";
import { zoneService } from "@/services/zone";
import { deskService } from "@/services/desk";
import { ObjectLiteral } from "@/types/misc";
import { Reservation, User } from "@/swagger/models";
import { FindResult } from "@/services/base";

// See https://github.com/kaorun343/vue-property-decorator for all options

@Component({
    name: "reservations-table",
    components: { ReservationCreateModal, Card, DateRangePicker, DataTable },
})
export default class ReservationsTable extends Vue {
    @Prop({ required: true })
    initialFilters: ReservationsTableInitialFilters;

    @Prop({ required: false })
    title: string;

    @Prop({ required: false })
    showCreateForUserId: number;

    filters: ReservationTableFilters = reservationTableFilters();
    fields = reservationTableFields();
    actions = reservationTableActions(this);

    result: ReservationTableResult = reservationTableResult();

    // Needed in the template for formatting
    DateTime = DateTime;
    CheckinFilterValues = CheckinFilterValues;

    refreshTimeout: NodeJS.Timeout;
    updateInterval: NodeJS.Timeout;
    updateIntervalSeconds = 30;

    created() {
        // Merge the passed filters and the default filters
        this.filters = { ...this.filters, ...this.initialFilters };

        // Hide fields based on the passed initial filters
        if (this.initialFilters.officeId) {
            this.hideField("office");
        }
        if (this.initialFilters.floorId) {
            this.hideField("office");
            this.hideField("floor");
        }
        if (this.initialFilters.zoneId) {
            this.hideField("office");
            this.hideField("floor");
            this.hideField("zone");
        }
        if (this.initialFilters.deskId) {
            this.hideField("office");
            this.hideField("floor");
            this.hideField("zone");
            this.hideField("desk");
        }
        if (this.initialFilters.userId) {
            this.hideField("user");
        }

        // Automatically update the results every X seconds
        this.updateInterval = setInterval(() => this.periodicUpdate(), this.updateIntervalSeconds * 1000);
    }

    /**
     * Cleanup the timeouts
     */
    destroyed() {
        clearTimeout(this.refreshTimeout);
        clearInterval(this.updateInterval);
    }

    /**
     * Hides a field by deleting it from this.fields
     * @param name Name of the field to hide
     */
    hideField(name: string) {
        // Maybe we should add a visible boolean on the field so we could unhide it again. Meh, fine for now
        this.fields = this.fields.filter((f) => f.field !== name);
    }

    /**
     * This is ran every X seconds to reload the results for a "real-time" view
     * Also checks if the page is visible, if not, it's a noop
     */
    async periodicUpdate() {
        if (document.hidden) return;
        this.queueRefresh();
    }

    /**
     * This is the method to call when you want to load the results. Debounces the actual call so that
     * quick calls to this do not spam the API
     */
    @Watch("filters", { deep: true, immediate: true })
    queueRefresh() {
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = setTimeout(() => this.refresh(), 100);
    }

    /**
     * Loads the reservations immediately,
     */
    async refresh() {
        try {
            this.result.loading = true;
            const res = await this.loadReservations();
            this.result.reservations = res.data;
            this.result.total = res.total;
        } catch (e) {
            console.warn(e);
        } finally {
            this.result.loading = false;
        }
    }

    /**
     * The underlying method that makes the request to the API and returns the result
     * @param extraAxiosOptions Any extra options to be passed to the Axios request
     */
    async loadReservations(extraAxiosOptions: ObjectLiteral = {}): Promise<FindResult<Reservation>> {
        return await reservationService.findNew({
            ...extraAxiosOptions,
            params: {
                limit: this.filters.itemsPerPage,
                offset: (this.filters.page - 1) * this.filters.itemsPerPage,
                customer: this.filters.customerId,
                office: this.filters.officeId,
                floor: this.filters.floorId,
                zone: this.filters.zoneId,
                desk: this.filters.deskId,
                user: this.filters.userId,
                after: this.filters.dates[0].toISODate(),
                before: this.filters.dates[1].toISODate(),
                ...this.checkinQueryParams,
                ...this.sortQueryParams,
            },
        });
    }

    /**
     * Makes a call to the backend for the results, and spits out a CSV that the browser will download.
     * The api ignores pagination when the accept header is set to 'text/csv'
     */
    async exportToCSV() {
        const res = await this.loadReservations({ headers: { accept: "text/csv" } });
        const csvString = res.data as unknown as string;

        const blob = new Blob([csvString], { type: "octet/stream" });
        const url = window.URL.createObjectURL(blob);

        // Oh, the wonders of the modern web :')
        const a = document.createElement("a");
        a.href = url;
        a.download = "reservations-export.csv";
        a.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Returns a nicely formatted query params object based on the specified sorting
     */
    get sortQueryParams(): ObjectLiteral<string> {
        const field = {
            office: "office.name",
            floor: "floor.level",
            zone: "zone.name",
            desk: "desk.name",
            user: "user.firstName",
            time: "checkinAt",
            date: "date",
        }[this.filters.sort.field];

        const order = { "1": "ASC", "-1": "DESC" }[this.filters.sort.direction.toString()];

        if (field && order) {
            return { [`sort[${field}]`]: order };
        } else {
            console.error("Invalid sort:", this.filters.sort);
            return {};
        }
    }

    /**
     * Returns a nicely formatted query params object based on the checkin filter value
     */
    get checkinQueryParams(): ObjectLiteral<string> {
        switch (this.filters.checkin) {
            case CheckinFilterValues.notCheckedIn:
                return { checkedIn: "false" };
            case CheckinFilterValues.checkedIn:
                return { checkedIn: "true" };
            case CheckinFilterValues.checkedInAndOut:
                return { checkedIn: "true", checkedOut: "true" };
            case CheckinFilterValues.checkedInNotOut:
                return { checkedIn: "true", checkedOut: "false" };
        }

        const field = {
            office: "office.name",
            floor: "floor.level",
            zone: "zone.name",
            desk: "desk.name",
            user: "user.firstName",
            time: "checkinAt",
            date: "date",
        }[this.filters.sort.field];

        const order = { "1": "ASC", "-1": "DESC" }[this.filters.sort.direction.toString()];

        if (field && order) {
            return { [`sort[${field}]`]: order };
        } else {
            console.error("Invalid sort:", this.filters.sort);
            return {};
        }
    }

    /**
     * Cancel a reservation, asks the user if they're sure
     * @param reservation The reservation in question
     */
    async cancelReservation(reservation: Reservation) {
        // @ts-ignore
        if (!this.$can("delete", "Reservation")) return alert("You do not have permission to cancel reservations.");
        if (!confirm(`Are you sure you want to cancel ${(reservation.user as User).firstName}'s reservation?`)) return;

        try {
            await reservationService.delete(reservation.id);
            await this.queueRefresh();
        } catch (e) {}
    }

    /**
     * Reload users when customer filter changes
     */
    @Watch("filters.customerId")
    async refreshUsers() {
        if (!this.filters.customerId) {
            this.result.users = [];
            return;
        }
        this.result.users = (await userService.findNew({ params: { customer: this.filters.customerId } })).data;
    }

    /**
     * Reload offices when customer filter changes
     */
    @Watch("filters.customerId")
    async refreshOffices() {
        if (!this.filters.customerId) {
            this.result.offices = [];
            return;
        }
        this.result.offices = (await officeService.findNew({ params: { customer: this.filters.customerId } })).data;
    }

    /**
     * Reload floors when office filter changes
     */
    @Watch("filters.officeId")
    async refreshFloors() {
        if (!this.filters.officeId) {
            this.result.floors = [];
            return;
        }
        this.result.floors = (await floorService.findNew({ params: { office: this.filters.officeId } })).data;
    }

    /**
     * Reload zones when floor filter changes
     */
    @Watch("filters.floorId")
    async refreshZones() {
        if (!this.filters.floorId) {
            this.result.zones = [];
            return;
        }
        this.result.zones = (await zoneService.findNew({ params: { floor: this.filters.floorId } })).data;
    }

    /**
     * Reload desks when zone filter changes
     */
    @Watch("filters.zoneId")
    async refreshDesks() {
        if (!this.filters.zoneId) {
            this.result.desks = [];
            return;
        }
        // prettier-ignore
        this.result.desks = (await deskService.findNew({ params: { zone: this.filters.zoneId, withDeleted: true } })).data;
    }
}
